import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus, BarChart3, TrendingUp, Wallet, Activity, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Import the new components we created
import { InsightsWidget } from '@/components/dashboard/widgets/InsightsWidget';
import { AiInsightsPanel } from '@/components/insights/AiInsightsPanel';
import { EnhancedFinancialChart } from '@/components/dashboard/charts/EnhancedFinancialChart';
import { RecentActivityWidget } from '@/components/dashboard/widgets/RecentActivityWidget';
import { QuickActionsWidget } from '@/components/dashboard/widgets/QuickActionsWidget';

// Import existing hooks and stores
import { useAuthStore } from '@/store/authStore';
import { useTransactionStore } from '@/store/transactionsStore';
import { useAccountsStore } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

// Create a simple direction hook until the import is fixed
function useDirection() {
  return { dir: 'ltr' }; // Default to LTR for now
}

interface AuthStore {
  user: { id: string } | null;
}

interface CurrencyStore {
  currencies: any[];
  fetchCurrencies: () => Promise<void>;
}

// Quick Metrics Card Component
const QuickMetricCard: React.FC<{
  title: string;
  value: string;
  change?: number;
  icon: React.ReactNode;
  color: string;
  isIncrease?: boolean;
}> = ({ title, value, change, icon, color, isIncrease }) => {
  // Define color mappings for consistent styling
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    '#439476': { 
      bg: 'bg-[#439476]/10 dark:bg-[#439476]/20', 
      text: 'text-[#439476]',
      border: 'border-[#439476]/20'
    },
    '#2196F3': { 
      bg: 'bg-[#2196F3]/10 dark:bg-[#2196F3]/20', 
      text: 'text-[#2196F3]',
      border: 'border-[#2196F3]/20'
    },
    '#FF9800': { 
      bg: 'bg-[#FF9800]/10 dark:bg-[#FF9800]/20', 
      text: 'text-[#FF9800]',
      border: 'border-[#FF9800]/20'
    },
    '#9C27B0': { 
      bg: 'bg-[#9C27B0]/10 dark:bg-[#9C27B0]/20', 
      text: 'text-[#9C27B0]',
      border: 'border-[#9C27B0]/20'
    }
  };

  const colorStyle = colorMap[color] || colorMap['#439476'];

  return (
    <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl p-4 transition-all hover:shadow-md hover:bg-background-secondary dark:hover:bg-background-dark-secondary group">
      <div className="flex items-center justify-between mb-3">
        <div className={cn(
          "flex items-center justify-center w-10 h-10 rounded-full transition-all group-hover:scale-105",
          colorStyle.bg
        )}>
          <div className={colorStyle.text}>
            {icon}
          </div>
        </div>
        {change !== undefined && (
          <div className={cn(
            "flex items-center text-xs font-medium px-2 py-1 rounded-full border",
            isIncrease 
              ? "text-positive bg-positive/10 dark:bg-positive/20 border-positive/20" 
              : "text-negative bg-negative/10 dark:bg-negative/20 border-negative/20"
          )}>
            <TrendingUp className={cn(
              "h-3 w-3 mr-1",
              !isIncrease && "rotate-180"
            )} />
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <h3 className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
        {title}
      </h3>
      <p className="text-lg font-bold text-text-primary dark:text-text-dark-primary">
        {value}
      </p>
    </div>
  );
};

export default function DashboardRefactored() {
  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('last3Months');
  const [areCurrenciesLoaded, setAreCurrenciesLoaded] = useState(false);
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const navigate = useNavigate();
  
  // Store hooks
  const { user } = useAuthStore() as AuthStore;
  const { transactions} = useTransactionStore() as any;
  const { accounts} = useAccountsStore() as any;
  const { 
    categories,
    loading: categoriesLoading,
    initialized: categoriesInitialized,
    fetchCategories
  } = useCategoriesStore();
  const { currencies} = useCurrenciesStore() as CurrencyStore;
  const { convertToDefault, defaultCurrency } = useCurrencyConversion();
  const { t } = useTranslation('common');
  const { dir } = useDirection();
  
  // Function to fetch ALL transactions for the selected period
  const fetchAllTransactionsForPeriod = useCallback(async (period: string) => {
    if (!user?.id || !areCurrenciesLoaded) return;

    setTransactionsLoading(true);
    try {
      // Get date range for the period to fetch data efficiently
      const now = new Date();
      let startDate: Date;
      
      switch (period) {
        case 'thisMonth':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'lastMonth':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          break;
        case 'last3Months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
          break;
        case 'last6Months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
          break;
        case 'thisYear':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        case 'allTime':
          // For all time, we'll fetch all transactions (this might be a lot, but needed for accurate reports)
          startDate = new Date(2020, 0, 1); // Start from a reasonable date
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      }

      console.log(`Fetching ALL transactions from ${startDate.toISOString()} for period: ${period}`);

      // Fetch ALL transactions for the period in batches to avoid memory issues
      let allFetchedTransactions: any[] = [];
      let hasMore = true;
      let cursor: string | null = null;
      const batchSize = 1000; // Fetch in larger batches for dashboard

      while (hasMore) {
        let query = supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', startDate.toISOString())
          .order('date', { ascending: false })
          .limit(batchSize);

        if (cursor) {
          query = query.lt('date', cursor);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching transactions:', error);
          throw error;
        }

        if (data && data.length > 0) {
          allFetchedTransactions = [...allFetchedTransactions, ...data];
          cursor = data[data.length - 1].date;
          hasMore = data.length === batchSize;
          
          console.log(`Fetched ${data.length} transactions, total: ${allFetchedTransactions.length}`);
        } else {
          hasMore = false;
        }
      }

      console.log(`Successfully fetched ${allFetchedTransactions.length} total transactions for dashboard`);
      setAllTransactions(allFetchedTransactions);
    } catch (error) {
      console.error('Error fetching all transactions for period:', error);
      // Fallback to using the limited transactions from store
      setAllTransactions(transactions || []);
    } finally {
      setTransactionsLoading(false);
    }
  }, [user?.id, areCurrenciesLoaded, transactions]);

  // Enhanced metrics using our custom hook with ALL transactions
  const metrics = useDashboardMetrics({
    transactions: allTransactions || [],
    accounts: accounts || [],
    convertToDefault,
    defaultCurrency,
    areCurrenciesLoaded,
    selectedPeriod
  });

  // Safe translation function
  const safeT = useCallback((key: string, options?: any): string => {
    const fallbacks: Record<string, string> = {
      'dashboard.overview.title': 'Financial Overview',
      'dashboard.seeAll': 'See all',
      'dashboard.addTransaction': 'Add Transaction',
      'dashboard.monthlyOverview': 'Monthly Overview',
      'dashboard.chartDescription': 'Track your income, expenses, and savings over time',
      'accounts.totalBalance': 'Total Balance',
      'dashboard.accounts': 'accounts',
      'dashboard.inflow': 'Inflow',
      'dashboard.outflow': 'Outflow',
      'dashboard.transactions': 'transactions',
      'dashboard.overview.periods.thisMonth': 'This Month',
      'dashboard.overview.periods.lastMonth': 'Last Month',
      'dashboard.overview.periods.last3Months': 'Last 3 Months',
      'dashboard.overview.periods.last6Months': 'Last 6 Months',
      'dashboard.overview.periods.allTime': 'All Time',
      'dashboard.overview.periods.thisYear': 'This Year',
      'dashboard.searchPlaceholder': 'Search...',
      'dashboard.enhanced': 'Enhanced',
      'dashboard.realTimeData': 'Real-time data',
      'dashboard.analytics': 'Analytics',
      'dashboard.quickActions': 'Quick Actions',
      'dashboard.metrics.dailyAvgSpending': 'Daily Avg Spending',
      'dashboard.metrics.monthOverMonthChange': 'Month-over-Month Change',
      'dashboard.metrics.savingsRate': 'Savings Rate',
      'dashboard.metrics.totalBalance': 'Total Balance',
      // Quick Actions fallbacks
      'dashboard.quickActions.title': 'Quick Actions',
      'dashboard.quickActions.subtitle': 'Common tasks and shortcuts',
      'dashboard.quickActions.actionsAvailable': 'actions',
      'dashboard.quickActions.addTransaction': 'Add Transaction',
      'dashboard.quickActions.addTransactionDesc': 'Record a new income or expense',
      'dashboard.quickActions.viewAnalytics': 'View Analytics',
      'dashboard.quickActions.viewAnalyticsDesc': 'Analyze your spending patterns',
      'dashboard.quickActions.manageBudgets': 'Manage Budgets',
      'dashboard.quickActions.manageBudgetsDesc': 'Set and track your spending goals',
      'dashboard.quickActions.manageAccounts': 'Manage Accounts',
      'dashboard.quickActions.manageAccountsDesc': 'Add or edit your bank accounts',
      'dashboard.quickActions.savingsGoals': 'Savings Goals',
      'dashboard.quickActions.savingsGoalsDesc': 'Track your savings progress',
      'dashboard.quickActions.reports': 'Generate Reports',
      'dashboard.quickActions.reportsDesc': 'Create detailed financial reports',
      'dashboard.quickActions.transfers': 'Transfer Money',
      'dashboard.quickActions.transfersDesc': 'Move money between accounts',
      'dashboard.quickActions.settings': 'Settings',
      'dashboard.quickActions.settingsDesc': 'Customize your preferences',
      'dashboard.quickActions.featured': 'Try Analytics Dashboard',
      'dashboard.quickActions.featuredDesc': 'Get deeper insights into your spending patterns',
      'dashboard.quickActions.tryNow': 'Try Now',
      // Recent Activity fallbacks
      'dashboard.recentActivity.title': 'Recent Activity',
      'dashboard.recentActivity.noTransactions': 'No recent transactions',
      'dashboard.recentActivity.addFirst': 'Add your first transaction to get started',
      'dashboard.recentActivity.viewAll': 'View All Transactions',
      // Financial Health Summary fallbacks
      'dashboard.healthSummary.title': 'Financial Health Summary',
      'dashboard.healthSummary.realTime': 'Real-time',
      'dashboard.healthSummary.dailyAvgSpending': 'Daily Avg Spending',
      'dashboard.healthSummary.monthOverMonthChange': 'Month-over-Month Change',
      'dashboard.healthSummary.savingsRate': 'Savings Rate',
      // Insights fallbacks
      'dashboard.insights.title': 'Financial Insights',
      'dashboard.insights.aiPowered': 'AI-Powered',
      'dashboard.insights.noInsights': 'Add more transactions to get personalized insights',
      'dashboard.insights.spendingIncrease': 'Spending Increased',
      'dashboard.insights.spendingDecrease': 'Great Progress!',
      'dashboard.insights.lowSavings': 'Low Savings Rate',
      'dashboard.insights.goodSavings': 'Excellent Savings!',
      'dashboard.insights.spendingProjection': 'Spending Projection',
      'dashboard.insights.quietWeek': 'Quiet Week',
      'dashboard.insights.spendingIncreaseDesc': 'Your expenses increased by {percentage}% this month',
      'dashboard.insights.spendingDecreaseDesc': 'You reduced spending by {percentage}% this month',
      'dashboard.insights.lowSavingsDesc': 'Your savings rate is {rate}%. Aim for at least 20%',
      'dashboard.insights.goodSavingsDesc': 'Your {rate}% savings rate is above the recommended 20%',
      'dashboard.insights.spendingProjectionDesc': 'At current rate, you\'ll spend {amount} this month',
      'dashboard.insights.quietWeekDesc': 'No transactions recorded in the past 7 days',
      'dashboard.insights.actions.reviewExpenses': 'Review your largest expenses',
      'dashboard.insights.actions.keepGoing': 'Keep up the good work',
      'dashboard.insights.actions.reduceExpenses': 'Consider reducing discretionary expenses',
      'dashboard.insights.actions.considerInvesting': 'Consider investing the excess',
      'dashboard.insights.actions.trackWeekly': 'Track progress weekly',
      'dashboard.insights.actions.logTransactions': 'Make sure to log all transactions',
      // Metrics fallbacks
      'dashboard.metrics.perDay': '/ day',
      'dashboard.metrics.increase': 'increase',
      'dashboard.metrics.decrease': 'decrease',
      'dashboard.metrics.ofIncome': 'of income',
      // Chart fallbacks
      'dashboard.forecast': 'Forecast',
      'dashboard.noDataAvailable': 'No data available for selected period',
      'dashboard.legend.income': 'Income',
      'dashboard.legend.expenses': 'Expenses',
      'dashboard.legend.netSavings': 'Net Savings',
      'dashboard.summary.totalIncome': 'Total Income',
      'dashboard.summary.totalExpenses': 'Total Expenses',
      'dashboard.summary.netSavings': 'Net Savings',
      // Missing period fallbacks for chart component
      'dashboard.thisMonth': 'This Month',
      'dashboard.lastMonth': 'Last Month',
      'dashboard.last3Months': 'Last 3 Months',
      'dashboard.last6Months': 'Last 6 Months',
      'dashboard.thisYear': 'This Year',
      'dashboard.allTime': 'All Time'
    };
    
    try {
      const translation = t(key, options);
      return typeof translation === 'string' ? translation : fallbacks[key] || key;
    } catch (e) {
      return fallbacks[key] || key.split('.').pop() || key;
    }
  }, [t]);

  // Currency formatting
  const formatCurrencyWithAbbrev = useCallback((value: number, currencyCode: string = defaultCurrency): string => {
    if (value === undefined || value === null || isNaN(value)) return `$0`;
    
    const absValue = Math.abs(value);
    let symbol = '$';
    
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency?.symbol) {
      symbol = currency.symbol;
    }
    
    if (absValue >= 1000000) {
      return `${symbol}${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      return `${symbol}${(absValue / 1000).toFixed(1)}K`;
    } else {
      return `${symbol}${absValue.toFixed(0)}`;
    }
  }, [currencies, defaultCurrency]);

  // Calculate total balance for quick metrics
  const totalBalance = useMemo(() => {
    if (!Array.isArray(accounts) || !areCurrenciesLoaded) return 0;
    
    return accounts.reduce((sum: number, acc: any) => {
      const balance = typeof acc.balance === 'number' ? acc.balance : 0;
      
      let accountCurrency = defaultCurrency;
      if (typeof acc.currency === 'string') {
        accountCurrency = acc.currency;
      } else if (acc.currency && typeof acc.currency === 'object' && 'code' in acc.currency) {
        accountCurrency = (acc.currency as any).code;
      }
      
      try {
        const convertedBalance = convertToDefault(balance, accountCurrency);
        return sum + convertedBalance;
      } catch (error) {
        return sum + balance;
      }
    }, 0);
  }, [accounts, areCurrenciesLoaded, convertToDefault, defaultCurrency]);

  // Check if currencies are loaded
  useEffect(() => {
    if (currencies && currencies.length > 0 && !areCurrenciesLoaded) {
      console.log(`Dashboard: ${currencies.length} currencies loaded`);
      setAreCurrenciesLoaded(true);
    }
  }, [currencies, areCurrenciesLoaded]);

  // Fetch all transactions when period changes or when currencies are loaded
  useEffect(() => {
    if (areCurrenciesLoaded && user?.id) {
      fetchAllTransactionsForPeriod(selectedPeriod);
    }
  }, [selectedPeriod, areCurrenciesLoaded, user?.id, fetchAllTransactionsForPeriod]);

  // Data loading check
  useEffect(() => {
    let mounted = true;
    
    const checkDataAvailability = () => {
      if (!mounted) return;
      
      const hasRequiredData = 
        !!user?.id && 
        !!accounts?.length && 
        !!currencies?.length && 
        Array.isArray(allTransactions) &&
        categoriesInitialized &&
        !transactionsLoading;
      
      if (hasRequiredData && !dataLoaded) {
        console.log('Dashboard: All essential data loaded');
        setDataLoaded(true);
        setIsLoading(false);
      }
    };
    
    if (!dataLoaded) {
      checkDataAvailability();
    }
    
    return () => {
      mounted = false;
    };
  }, [
    user?.id,
    accounts,
    currencies,
    allTransactions,
    categoriesInitialized,
    dataLoaded,
    transactionsLoading
  ]);

  // Dynamic monthly data for chart based on selected period
  const monthlyData = useMemo(() => {
    if (!Array.isArray(allTransactions) || !allTransactions.length || !areCurrenciesLoaded) return [];
    
    const now = new Date();
    
    // Get date range based on selected period
    const getDateRange = () => {
      switch (selectedPeriod) {
        case 'thisMonth':
          return {
            start: new Date(now.getFullYear(), now.getMonth(), 1),
            end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
            months: 1
          };
        case 'lastMonth':
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          return {
            start: lastMonth,
            end: new Date(now.getFullYear(), now.getMonth(), 0),
            months: 1
          };
        case 'last3Months':
          return {
            start: new Date(now.getFullYear(), now.getMonth() - 2, 1),
            end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
            months: 3
          };
        case 'last6Months':
          return {
            start: new Date(now.getFullYear(), now.getMonth() - 5, 1),
            end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
            months: 6
          };
        case 'thisYear':
          return {
            start: new Date(now.getFullYear(), 0, 1),
            end: new Date(now.getFullYear(), 11, 31),
            months: 12
          };
        case 'allTime':
          // Get earliest transaction date
          const earliestDate = allTransactions.reduce((earliest, t) => {
            const tDate = new Date(t.date);
            return tDate < earliest ? tDate : earliest;
          }, now);
          return {
            start: new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1),
            end: now,
            months: Math.max(3, Math.ceil((now.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24 * 30)))
          };
        default:
          return {
            start: new Date(now.getFullYear(), now.getMonth() - 2, 1),
            end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
            months: 3
          };
      }
    };

    const { start, end, months } = getDateRange();
    const chartData = [];

    // Generate monthly data points
    for (let i = 0; i < Math.min(months, 12); i++) {
      const monthStart = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const monthEnd = new Date(start.getFullYear(), start.getMonth() + i + 1, 0);
      
      // Skip if month is in the future
      if (monthStart > now) continue;

      const monthTransactions = allTransactions.filter(t => {
        const tDate = new Date(t.date);
        return tDate >= monthStart && tDate <= monthEnd;
      });

      const income = monthTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => {
          const account = accounts.find((a: any) => a.id === t.account_id);
          if (!account) return sum;
          
          const amount = typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount;
          if (isNaN(amount)) return sum;
          
          const currency = t.currency || account.currency || defaultCurrency;
          
          try {
            return sum + convertToDefault(Math.abs(amount), currency);
          } catch (error) {
            return sum + Math.abs(amount);
          }
        }, 0);

      const expenses = monthTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => {
          const account = accounts.find((a: any) => a.id === t.account_id);
          if (!account) return sum;
          
          const amount = typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount;
          if (isNaN(amount)) return sum;
          
          const currency = t.currency || account.currency || defaultCurrency;
          
          try {
            return sum + convertToDefault(Math.abs(amount), currency);
          } catch (error) {
            return sum + Math.abs(amount);
          }
        }, 0);

      chartData.push({
        month: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        income,
        expenses,
        savings: income - expenses
      });
    }

    return chartData;
  }, [allTransactions, accounts, selectedPeriod, convertToDefault, defaultCurrency, areCurrenciesLoaded]);

  // Loading state
  if (isLoading || !dataLoaded || categoriesLoading || transactionsLoading) {
    return (
      <div className="w-full px-4 sm:px-8 py-8 space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <Skeleton className="h-8 w-[150px] mb-4 md:mb-0" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
        {transactionsLoading && (
          <div className="text-center py-4">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Loading all transactions for accurate reports...
            </p>
          </div>
        )}
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-64 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background-primary dark:bg-background-dark-primary">
      {/* Enhanced Header */}
      <div className="sticky top-0 z-40 border-b border-border/50 dark:border-border-dark/50 bg-background-secondary/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150">
        <div className="px-4 sm:px-8 py-4 sm:py-6">
          {/* Title and Description */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex-shrink-0">
              <div className="flex items-center space-x-3 rtl:space-x-reverse mb-2">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                  {safeT('dashboard.overview.title')}
                </h1>
                <div className="bg-gradient-to-r from-secondary to-primary text-white text-xs px-2 py-1 rounded-full font-medium shadow-sm">
                  {safeT('dashboard.enhanced')}
                </div>
              </div>
              <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-text-secondary dark:text-text-dark-secondary">
                <span className="flex items-center space-x-1 rtl:space-x-reverse">
                  <div className="w-2 h-2 bg-secondary dark:bg-primary-dark rounded-full animate-pulse"></div>
                  <span>{format(new Date(), 'MMMM dd, yyyy')}</span>
                </span>
                <span className="hidden md:flex items-center space-x-1 rtl:space-x-reverse">
                  <div className="w-2 h-2 bg-primary dark:bg-secondary-dark rounded-full"></div>
                  <span>{safeT('dashboard.realTimeData')}</span>
                </span>
                {allTransactions.length > 0 && (
                  <span className="hidden lg:flex items-center space-x-1 rtl:space-x-reverse text-xs">
                    <div className="w-2 h-2 bg-positive rounded-full"></div>
                    <span>{allTransactions.length} transactions loaded</span>
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons - Desktop */}
            <div className="hidden sm:flex items-center space-x-3 rtl:space-x-reverse">
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary hover:bg-background-secondary dark:hover:bg-background-dark-secondary rounded-xl shadow-sm h-11 px-3 text-sm font-medium focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:border-transparent"
              >
                <option value="thisMonth">{safeT('dashboard.overview.periods.thisMonth')}</option>
                <option value="lastMonth">{safeT('dashboard.overview.periods.lastMonth')}</option>
                <option value="last3Months">{safeT('dashboard.overview.periods.last3Months')}</option>
                <option value="last6Months">{safeT('dashboard.overview.periods.last6Months')}</option>
                <option value="thisYear">{safeT('dashboard.overview.periods.thisYear')}</option>
                <option value="allTime">{safeT('dashboard.overview.periods.allTime')}</option>
              </select>
              
              <Button 
                onClick={() => navigate('/app/analytics')}
                variant="outline" 
                className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm h-11 px-4"
              >
                <BarChart3 className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {safeT('dashboard.analytics')}
              </Button>

              <Button
                onClick={() => navigate('/app/transactions?add=true')}
                className="shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 h-11 px-6"
              >
                <Plus className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {safeT('dashboard.addTransaction')}
              </Button>
            </div>
          </div>
          
          {/* Mobile Actions */}
          <div className="flex sm:hidden flex-col gap-3">
            {/* Period Selector - Mobile */}
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary hover:bg-background-secondary dark:hover:bg-background-dark-secondary rounded-xl shadow-sm h-11 px-3 text-sm font-medium focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark focus:border-transparent"
            >
              <option value="thisMonth">{safeT('dashboard.overview.periods.thisMonth')}</option>
              <option value="lastMonth">{safeT('dashboard.overview.periods.lastMonth')}</option>
              <option value="last3Months">{safeT('dashboard.overview.periods.last3Months')}</option>
              <option value="last6Months">{safeT('dashboard.overview.periods.last6Months')}</option>
              <option value="thisYear">{safeT('dashboard.overview.periods.thisYear')}</option>
              <option value="allTime">{safeT('dashboard.overview.periods.allTime')}</option>
            </select>

            {/* Action Buttons - Mobile */}
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate('/app/analytics')}
                variant="outline" 
                className="flex-1 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm h-11"
              >
                <BarChart3 className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {safeT('dashboard.analytics')}
              </Button>
              
              <Button
                onClick={() => navigate('/app/transactions?add=true')}
                className="flex-1 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 h-11"
              >
                <Plus className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {safeT('dashboard.addTransaction')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-8 py-6 space-y-6">
        {/* Quick Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickMetricCard
            title={safeT('dashboard.metrics.totalBalance')}
            value={formatCurrencyWithAbbrev(totalBalance, defaultCurrency)}
            icon={<Wallet className="h-5 w-5" />}
            color="#439476"
          />
          <QuickMetricCard
            title={safeT('dashboard.metrics.dailyAvgSpending')}
            value={formatCurrencyWithAbbrev(metrics.dailyAvgSpending, defaultCurrency)}
            icon={<Activity className="h-5 w-5" />}
            color="#2196F3"
          />
          <QuickMetricCard
            title={safeT('dashboard.metrics.monthOverMonthChange')}
            value={`${Math.abs(metrics.monthOverMonthChange).toFixed(1)}%`}
            change={metrics.monthOverMonthChange}
            isIncrease={metrics.isIncrease}
            icon={<TrendingUp className="h-5 w-5" />}
            color="#FF9800"
          />
          <QuickMetricCard
            title={safeT('dashboard.metrics.savingsRate')}
            value={`${metrics.savingsRate.toFixed(1)}%`}
            icon={<Target className="h-5 w-5" />}
            color="#9C27B0"
          />
        </div>

        {/* Quick Actions Widget */}
        <QuickActionsWidget safeT={safeT} />

        {/* Enhanced Financial Chart - Monthly Overview */}
        <EnhancedFinancialChart
          data={monthlyData}
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          formatCurrency={formatCurrencyWithAbbrev}
          safeT={safeT}
        />

        {/* Recent Activity Widget */}
        <RecentActivityWidget
          transactions={allTransactions || []}
          accounts={accounts || []}
          categories={categories || []}
          formatCurrencyWithAbbrev={formatCurrencyWithAbbrev}
          safeT={safeT}
          defaultCurrency={defaultCurrency}
          convertToDefault={convertToDefault}
        />

        {/* AI-generated spending insights (server-side, cached per month) */}
        <AiInsightsPanel compact />

        {/* Financial Insights Widget */}
        <InsightsWidget
          transactions={allTransactions || []}
          accounts={accounts || []}
          monthOverMonthChange={metrics.monthOverMonthChange}
          savingsRate={metrics.savingsRate}
          dailyAvgSpending={metrics.dailyAvgSpending}
          formatCurrencyWithAbbrev={formatCurrencyWithAbbrev}
          safeT={safeT}
          selectedPeriod={selectedPeriod}
        />
      </div>
      
    </div>
  );
} 